import { isAfter, isBefore, isSameDay, isValid, parseISO } from "date-fns";

export type DateInput = string | Date | { toDate: () => Date };

/**
 * Use instant comparison (isDateAfter, isDateBefore, isSameDate, etc.) when you
 * only care whether a moment in time has passed — e.g. sale windows, event
 * start/end, "is this in the past?". For calendar-day comparison in a specific
 * timezone, use calendar-day-comparisons.ts instead.
 */

// Define the specific structure for a Moment-like object
interface MomentLike {
  toDate: () => Date;
}

/**
 * Type guard to check if an object is Moment-like
 */
const isMomentLike = (date: unknown): date is MomentLike => {
  return (
    typeof date === "object" &&
    date !== null &&
    "toDate" in date &&
    typeof (date as MomentLike).toDate === "function"
  );
};

/**
 * Internal helper to handle string, Moment-like, or Date inputs consistently
 */
export const ensureDate = (date: DateInput | null | undefined): Date => {
  if (!date) return new Date(NaN);
  if (date instanceof Date) return date;
  if (typeof date === "string") return parseISO(date);

  // Type guard for Moment-like objects
  if (isMomentLike(date)) {
    return date.toDate();
  }

  return new Date(NaN);
};

/**
 * Coerces API-style date input (string, `Date`, or Moment-like) to a `Date`, or
 * `undefined` when the value is missing or does not represent a finite instant.
 *
 * Use when optional props or form fields should omit the date entirely rather
 * than passing an invalid `Date` — e.g. `initialDate` on pickers, or chaining
 * after optional API fields. Prefer {@link ensureDate} when you always need a
 * `Date` instance (including invalid-distinguishable `NaN` time).
 */
export function ensureDateOrUndefined(
  date: DateInput | null | undefined
): Date | undefined {
  if (date == null) return undefined;
  const d = ensureDate(date);
  return Number.isFinite(d.getTime()) ? d : undefined;
}

/**
 * Check if date1 is after date2 (instant comparison).
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isDateAfter(date1: DateInput, date2: DateInput): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return isAfter(d1, d2);
}

/**
 * Check if date1 is before date2 (instant comparison).
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isDateBefore(date1: DateInput, date2: DateInput): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return isBefore(d1, d2);
}

/**
 * Check if two dates are on the same local calendar day (ignoring time).
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isSameDate(date1: DateInput, date2: DateInput): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return isSameDay(d1, d2);
}

/**
 * Check if a date is today (local calendar day).
 * Callers must ensure date is defined; handle null/undefined before calling.
 */
export function isDateToday(date: DateInput): boolean {
  const d = ensureDate(date);
  if (!isValid(d)) return false;
  return isSameDay(d, new Date());
}

/**
 * Is date1 on the same or an earlier local calendar day than date2?
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isSameOrBeforeDate(
  date1: DateInput,
  date2: DateInput
): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return d1 < d2 || isSameDay(d1, d2);
}

/**
 * Is date1 on the same or a later local calendar day than date2?
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isSameOrAfterDate(date1: DateInput, date2: DateInput): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return d1 > d2 || isSameDay(d1, d2);
}
